/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState } from "react";
import { Box } from "@mui/material";
import GET_CHATBOT_ANALYTICS from "src/graphql/query/chatbot/getChatbotAnalytics";
import { useQuery, useSubscription } from "@apollo/client";
import ActiveWidgetsIcon from "src/assets/images/dashboard/chatbot_active_widgets_circular.png";
import ClosedChatsIcon from "src/assets/images/dashboard/chatbot_closed_chats_icon.png";
import BotHandoffsIcon from "src/assets/images/dashboard/chatbot_handoffs_icon.svg";
import WidgetCustomersIcon from "src/assets/images/dashboard/chatbot_customers_icon.svg";
import CHATBOT_ANALYTICS_UPDATED from "src/graphql/subscriptions/chatbot/chatbotAnalytics";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { UseDashboardContext } from "src/context/dashboard";
import { useFeature } from "src/context/global";
import GeneralCard from "../FigureCards/GeneralCard";

const ChatbotFigureCards = () => {
  const {
    mainSuperAdminId,
    agentId: loggedInUserId,
    panelType,
  } = useActiveUserDetail();
  const { canUse } = useFeature();
  const canSeeChatbot = canUse("see_chatbot_conversations");

  const contextData = UseDashboardContext();
  let globalTimeFrame: string = "today";
  let storedAgentId: number | null = null;

  if (contextData) {
    ({ globalTimeFrame, storedAgentId } = contextData);
  }

  // If viewing self, pass null to trigger "Pool" logic.
  // If viewing others, pass their specific agentId.
  const targetAgentId =
    storedAgentId && Number(storedAgentId) !== Number(loggedInUserId)
      ? Number(storedAgentId)
      : null;

  const { data, loading, refetch } = useQuery(GET_CHATBOT_ANALYTICS, {
    variables: {
      timeFrame: globalTimeFrame,
      agentId: targetAgentId,
    },
    fetchPolicy: "network-only",
  });

  const [metrics, setMetrics] = useState({
    activeWidgets: 0,
    botConversions: 0,
    handoffs: 0,
    widgetCustomers: 0,
  });

  React.useEffect(() => {
    if (data?.getChatbotAnalytics) {
      setMetrics(data.getChatbotAnalytics);
    }
  }, [data]);

  // Real-time updates subscription - Intelligent Refetching
  useSubscription(CHATBOT_ANALYTICS_UPDATED, {
    variables: {
      mainSuperAdminId: Number(mainSuperAdminId),
      agentId: Number(targetAgentId || loggedInUserId),
    },
    skip: !canSeeChatbot,
    onData: (options) => {
      const payload = options.data.data?.chatbotAnalyticsUpdated;
      if (!payload || globalTimeFrame !== "today") return;

      const triggeredByAgentId = Number(payload.agentId);
      const isMe = triggeredByAgentId === Number(loggedInUserId);
      const isAdminOrManager = panelType !== "AGENT";

      // 1. Always refetch if it's my own action
      if (isMe) {
        refetch();
        return;
      }

      // 2. Admins looking at the "Pool" (targetAgentId is null) should refetch on any change
      if (isAdminOrManager && !targetAgentId) {
        refetch();
        return;
      }

      // 3. Admins looking at a SPECIFIC agent should only refetch if that agent triggered it
      if (targetAgentId && triggeredByAgentId === Number(targetAgentId)) {
        refetch();
      }
    },
  });

  // Styles matching FigureCards pattern
  const rootContainerStyle = {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "24px",
    width: "100%",
    boxSizing: "border-box" as const,
  };

  const flexItemStyle = {
    flex: "1 1 250px", // Standardize flex-basis for 4-card layout
    borderRadius: "14px",
    backgroundColor: "#FFFFFF",
    boxShadow: "6px 6px 54px 0px #0000000D",
    boxSizing: "border-box" as const, // Ensure box-sizing is consistent
  };

  // if (!canSeeChatbot) return null;

  return (
    <Box sx={rootContainerStyle}>
      <Box sx={flexItemStyle} id="tour-dashboard-active-widgets">
        <GeneralCard
          count={metrics.activeWidgets}
          loader={loading}
          title="Active Widgets"
          cardImage={ActiveWidgetsIcon}
          dropDownValues={globalTimeFrame}
          hideDropdown
        />
      </Box>
      <Box sx={flexItemStyle} id="tour-dashboard-closed-bot-chats">
        <GeneralCard
          count={metrics.botConversions}
          loader={loading}
          title="Closed Bot Chats"
          cardImage={ClosedChatsIcon}
          dropDownValues={globalTimeFrame}
          hideDropdown
        />
      </Box>
      <Box sx={flexItemStyle} id="tour-dashboard-bot-handoffs">
        <GeneralCard
          count={metrics.handoffs}
          loader={loading}
          title="Bot Handoffs"
          cardImage={BotHandoffsIcon}
          dropDownValues={globalTimeFrame}
          hideDropdown
        />
      </Box>
      <Box sx={flexItemStyle} id="tour-dashboard-widget-customers">
        <GeneralCard
          count={metrics.widgetCustomers}
          loader={loading}
          title="Widget Customers"
          cardImage={WidgetCustomersIcon}
          dropDownValues={globalTimeFrame}
          hideDropdown
        />
      </Box>
    </Box>
  );
};

export default ChatbotFigureCards;
